import React from 'react';

import { useTranslation } from 'react-i18next';

import AddPhotoCocktailForm from '../components/AddPhotoCocktailForm';
import BannerSelector from '../components/BannerSelector';
import PhotoCocktailComponent from '../components/PhotoCocktailComponent';
import useMenuContext from '../hooks/useMenuContext';
import { useBlocksContext } from '../hooks/useBlocksContext';
import { useConfigContext } from '../hooks/useConfigContext';
import useSearchPoolContext from '../hooks/useSearchPoolContext';
import { BEVERAGE_TYPES } from '../lib/constants';

import './PhotoCocktailScreen.scss';

const { PHOTO_COCKTAIL } = BEVERAGE_TYPES;

const PHOTO_COCKTAILS_PER_PAGE = 8;
const PHOTO_COCKTAILS_PER_PAGE_SUMMER_MENU = 4;
const MAX_PAGES_SUMMER_MENU = 3;
const PHOTO_COCKTAILS_PER_PAGE_WINTER_MENU = 2;
const MAX_PAGES_WINTER_MENU = 2;


export default function PhotoCocktailScreen() {
  const { t } = useTranslation();
  const [draggedIndex, setDraggedIndex] = React.useState(null);
  const [dropTargetIndex, setDropTargetIndex] = React.useState(null);

  const { unfitBanners } = useBlocksContext();

  const {
    selectedBlockFormat,
  } = useConfigContext();

  let maxCocktails = 0; // 0 = no limit
  if (selectedBlockFormat === 'A5_summer') {
    maxCocktails = PHOTO_COCKTAILS_PER_PAGE_SUMMER_MENU * MAX_PAGES_SUMMER_MENU;
  } else if (selectedBlockFormat === 'A5_winter') {
    maxCocktails = PHOTO_COCKTAILS_PER_PAGE_WINTER_MENU * MAX_PAGES_WINTER_MENU;
  }

  let cocktailsPerPage = PHOTO_COCKTAILS_PER_PAGE;
  if (selectedBlockFormat === 'A5_summer') {
    cocktailsPerPage = PHOTO_COCKTAILS_PER_PAGE_SUMMER_MENU;
  } else if (selectedBlockFormat === 'A5_winter') {
    cocktailsPerPage = PHOTO_COCKTAILS_PER_PAGE_WINTER_MENU;
  }

  const {
    addBeverage,
    deleteBeverage,
    updateBeverage,
    movePhotoCocktail,
    photoCocktailBanners,

    photoCocktails,

    photoCocktailBannersAllowed,
  } = useMenuContext();

  const { beveragesSearchPool } = useSearchPoolContext();

  const clearDragState = () => {
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const handleDragStart = (event, index) => {
    setDraggedIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();

    if (draggedIndex === null || draggedIndex === index) {
      return;
    }

    event.dataTransfer.dropEffect = 'move';
    setDropTargetIndex(index);
  };

  const handleDrop = async (event, index) => {
    event.preventDefault();

    const oldIndexFromTransfer = event.dataTransfer.getData('text/plain');
    const parsedOldIndex = oldIndexFromTransfer === ''
      ? null
      : Number(oldIndexFromTransfer);
    const oldIndex = Number.isInteger(parsedOldIndex)
      ? parsedOldIndex
      : draggedIndex;

    clearDragState();

    if (oldIndex === null || oldIndex < 0 || oldIndex >= photoCocktails.length) {
      return;
    }

    try {
      await movePhotoCocktail(oldIndex, index);
    } catch (error) {
      window.alert(t('itemUpdateError_plural'));
    }
  };

  return (
    <div className="container">
      <div className="row">
        <div className="col-12">
          <div className="row">
            {photoCocktails.length ? (
              photoCocktails.map((photoCocktail, index) => (
                <React.Fragment key={photoCocktail.id}>
                  <div className="col-12 col-lg-6 mb-4">
                    <PhotoCocktailComponent
                      photoCocktail={photoCocktail}
                      onDelete={deleteBeverage}
                      onUpdate={updateBeverage}
                      onDragStart={handleDragStart}
                      onDragEnd={clearDragState}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      index={index}
                      isDropTarget={dropTargetIndex === index}
                    />
                  </div>

                  {Boolean(index) && !((index + 1) % cocktailsPerPage) && (
                    <div className="page-divider mb-4 bg-info col-12">
                      <div className="badge rounded-pill bg-info page-divider__inner text-white">
                        {t('page')}: {(index + 1) / cocktailsPerPage}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))
            ) : (
              <div className="col-12">
                <p className="text-center">{t('emptyList')}</p>
              </div>
            )}
          </div>

          <AddPhotoCocktailForm
            onSubmit={addBeverage}
            searchPool={beveragesSearchPool[PHOTO_COCKTAIL]}
            hideForm={maxCocktails && photoCocktails.length >= maxCocktails}
          />

          {photoCocktailBannersAllowed && (
            <BannerSelector
              banners={photoCocktailBanners}
              type={PHOTO_COCKTAIL}
              unfitBanners={unfitBanners}
            />
          )}
        </div>
      </div>
    </div>
  );
}
